import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const CompanyCtx = createContext(null);

const SCOPE_KEY = "invoiceforge.companyScope";

/**
 * Read the saved read-scope selection.
 *
 * Scoped to one browser profile only: it is a viewing preference, not data, so
 * it is deliberately not stored server-side. A malformed or absent value falls
 * back to "active", which is the behaviour that predates this control.
 */
function readStoredScope() {
  try {
    const raw = window.localStorage.getItem(SCOPE_KEY);
    if (!raw) return { mode: "active", ids: [] };
    const parsed = JSON.parse(raw);
    const mode = ["active", "all", "custom"].includes(parsed?.mode)
      ? parsed.mode
      : "active";
    const ids = Array.isArray(parsed?.ids)
      ? parsed.ids.filter((v) => typeof v === "string" && v)
      : [];
    return { mode, ids };
  } catch {
    return { mode: "active", ids: [] };
  }
}

/**
 * Tracks the account's company profiles, which one is active, and which
 * companies the workspace is currently *viewing*.
 *
 * Two separate concepts, deliberately not merged:
 *
 * - The **active company** is persisted server-side as
 *   `users.default_company_id`. It decides which company's details are printed
 *   on a newly created invoice or receipt.
 * - The **view scope** (`scopeMode`/`scopeIds`) is a local filter that decides
 *   which companies' documents appear in lists and which companies the
 *   dashboard totals cover. It supports one company, several, or all.
 *
 * A generation counter (`version`) is bumped on switch, and `scopeVersion` on a
 * scope change, so pages can refetch when either moves.
 */
export function CompanyProvider({ children }) {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [defaultId, setDefaultId] = useState("");
  const [limit, setLimit] = useState(3);
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);
  const [{ mode: scopeMode, ids: scopeIds }, setScope] = useState(readStoredScope);
  const [scopeVersion, setScopeVersion] = useState(0);

  const persistScope = useCallback((next) => {
    setScope(next);
    setScopeVersion((v) => v + 1);
    try {
      window.localStorage.setItem(SCOPE_KEY, JSON.stringify(next));
    } catch {
      // Private browsing or a full quota: the selection still applies for this
      // session, it just will not survive a reload.
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/companies");
      setCompanies(data?.companies || []);
      setDefaultId(data?.default_company_id || "");
      setLimit(data?.company_limit ?? 3);
    } catch {
      // A 401 here is already handled by the auth interceptor; leaving the
      // list empty keeps the switcher hidden rather than showing a broken state.
      setCompanies([]);
      setDefaultId("");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) refresh();
    else {
      setCompanies([]);
      setDefaultId("");
      setLoading(false);
    }
  }, [user, refresh]);

  const activeCompany = useMemo(
    () => companies.find((c) => c.id === defaultId) || companies[0] || null,
    [companies, defaultId],
  );

  /**
   * Drop ids that no longer belong to the account (a company was deleted, or
   * this scope was copied from another account). Without this, a stale id would
   * be sent to the API and come back as a 404.
   */
  const validScopeIds = useMemo(
    () => scopeIds.filter((id) => companies.some((c) => c.id === id)),
    [scopeIds, companies],
  );

  /** Every company id on the account, in display order. */
  const allCompanyIds = useMemo(
    () => companies.map((c) => c.id).filter(Boolean),
    [companies],
  );

  /**
   * The resolved selection. `all` means no company filter at all, which is
   * sent as all_companies=true rather than a long id list.
   */
  const effectiveScopeIds = useMemo(() => {
    if (scopeMode === "all") return allCompanyIds;
    if (scopeMode === "custom") return validScopeIds;
    return defaultId ? [defaultId] : [];
  }, [scopeMode, allCompanyIds, validScopeIds, defaultId]);

  // A custom selection covering every company is treated as "all" so the UI
  // reports one scope instead of an N-company list that happens to be complete.
  const scopeIsAll =
    scopeMode === "all" ||
    (scopeMode === "custom" &&
      allCompanyIds.length > 0 &&
      validScopeIds.length === allCompanyIds.length);

  /** Query params to merge into every company-scoped API call. */
  const scopeParams = useMemo(() => {
    if (scopeIsAll) return { all_companies: true };
    if (!effectiveScopeIds.length) return {};
    return { company_ids: effectiveScopeIds.join(",") };
  }, [scopeIsAll, effectiveScopeIds]);

  const scopeLabel = useMemo(() => {
    if (scopeIsAll) return "All companies";
    if (!effectiveScopeIds.length) return "No company";
    if (scopeMode === "active" || effectiveScopeIds.length === 1) {
      const match = companies.find((c) => c.id === effectiveScopeIds[0]);
      return match?.name || "1 company";
    }
    return `${effectiveScopeIds.length} companies`;
  }, [scopeIsAll, scopeMode, effectiveScopeIds, companies]);

  /** Companies in the scope, for the dashboard's currency/label lookups. */
  const scopedCompanies = useMemo(
    () => companies.filter((c) => effectiveScopeIds.includes(c.id)),
    [companies, effectiveScopeIds],
  );

  const setScopeMode = useCallback(
    (mode) => persistScope(
      // "custom" has to be seeded with something real, otherwise it resolves to
      // an empty selection, which the API would read as "no filter" = all.
      { mode, ids: mode === "custom" ? allCompanyIds : [] },
    ),
    [persistScope, allCompanyIds],
  );

  const setAllScope = useCallback(() => persistScope({ mode: "all", ids: [] }), [persistScope]);

  const setActiveScope = useCallback(() => persistScope({ mode: "active", ids: [] }), [persistScope]);

  /**
   * Toggle one company, promoting to "custom".
   *
   * Works from the *resolved* selection rather than the stored id list, because
   * in "all" mode the stored list is empty by design. Ticking a box off while
   * everything is selected must therefore leave the other companies selected,
   * not collapse the scope down to the one that was unticked.
   */
  const toggleScopeCompany = useCallback(
    (companyId) => {
      const base = effectiveScopeIds.includes(companyId)
        ? effectiveScopeIds.filter((id) => id !== companyId)
        : [...effectiveScopeIds, companyId];
      if (!base.length) {
        // Never leave the scope empty: with no filter at all the API would read
        // it as "all companies", which is not what unticking the last box means.
        persistScope({ mode: "active", ids: [] });
        return;
      }
      const complete = base.length === allCompanyIds.length &&
        base.every((id) => allCompanyIds.includes(id));
      persistScope(complete ? { mode: "all", ids: [] } : { mode: "custom", ids: base });
    },
    [effectiveScopeIds, allCompanyIds, persistScope],
  );

  const switchCompany = useCallback(
    async (companyId) => {
      if (!companyId || companyId === defaultId) return;
      try {
        const { data } = await api.post(`/companies/${companyId}/default`);
        const nextCompanies = data?.companies || [];
        const nextId = data?.default_company_id || companyId;
        const next = nextCompanies.find((c) => c.id === nextId);
        setCompanies(nextCompanies);
        setDefaultId(nextId);
        // Bump so company-scoped pages refetch for the new workspace.
        setVersion((v) => v + 1);
        // Name the company that was switched *to*. Reading activeCompany here
        // would report the outgoing one, since state has not committed yet.
        toast.success(`Switched to ${next?.name || "company"}`);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    },
    [defaultId],
  );

  /**
   * Show exactly one company. Used by dashboard per-company shortcuts, where
   * "open this company's invoices" means the list should already be filtered to
   * it rather than landing on the unfiltered list.
   */
  const focusScopeCompany = useCallback(
    (companyId) => persistScope({ mode: "custom", ids: [companyId] }),
    [persistScope],
  );

  const value = useMemo(
    () => ({
      companies,
      activeCompany,
      companyId: activeCompany?.id || "",
      defaultId,
      limit,
      used: companies.length,
      canAddCompany: companies.length < limit,
      loading,
      version,
      refresh,
      switchCompany,
      // Read-scope: which companies the workspace is currently showing.
      scopeMode,
      scopeIds: effectiveScopeIds,
      scopeIsAll,
      scopeLabel,
      scopeParams,
      scopeVersion,
      scopedCompanies,
      setScopeMode,
      setAllScope,
      setActiveScope,
      focusScopeCompany,
      toggleScopeCompany,
    }),
    [
      companies, activeCompany, defaultId, limit, loading, version, refresh,
      switchCompany, scopeMode, effectiveScopeIds, scopeIsAll, scopeLabel,
      scopeParams, scopeVersion, scopedCompanies, setScopeMode, setAllScope,
      setActiveScope, focusScopeCompany, toggleScopeCompany,
    ],
  );

  return <CompanyCtx.Provider value={value}>{children}</CompanyCtx.Provider>;
}

export const useCompanies = () => {
  const ctx = useContext(CompanyCtx);
  if (!ctx) throw new Error("useCompanies must be used inside <CompanyProvider>");
  return ctx;
};